// src/pages/DrugReferencePage.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Badge,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Search,
  LocalHospital,
  MedicalInformation,
  Warning,
  Description,
  OpenInNew,
  Download,
  Link as LinkIcon,
  Info,
  Medication,
  Timeline,
  NoteAdd,
  ContentCopy,
  CheckCircle,
  Error,
  ExpandMore,
  Science,
  SafetyDivider,
  Inventory,
  CalendarToday,
  School,
  Psychology,
} from "@mui/icons-material";
import { getDrugReference } from "../services/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ListItemIcon from "@mui/material/ListItemIcon";
export default function DrugReferencePage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [drugData, setDrugData] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const theme = useTheme();
  const { t } = useTranslation();

  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem("drugReferenceRecentSearches");
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  // Save to recent searches
  const saveToRecentSearches = (drugName) => {
    const newSearch = {
      name: drugName,
      timestamp: new Date().toISOString(),
    };

    const updatedSearches = [
      newSearch,
      ...recentSearches.filter(s => s.name !== drugName).slice(0, 4),
    ];

    setRecentSearches(updatedSearches);
    localStorage.setItem("drugReferenceRecentSearches", JSON.stringify(updatedSearches));
  };

  // Search for drug reference
  const handleSearch = async (e) => {
    e?.preventDefault();

    if (!searchTerm.trim()) {
      toast.error(t('drugReference.enterDrugName'));
      return;
    }

    setSearching(true);
    setError("");
    setDrugData(null);

    try {
      const response = await getDrugReference(searchTerm.trim());

      if (response.data?.success && response.data?.reference) {
        setDrugData(response.data.reference);
        saveToRecentSearches(searchTerm.trim());
        toast.success(t('drugReference.foundInfo', { name: searchTerm }));
      } else {
        setError(t('drugReference.noInfoFound'));
        toast.warning(t('drugReference.noDetailedInfo'));
      }
    } catch (err) {
      console.error("Drug search error:", err);
      setError(err.response?.data?.message || t('drugReference.failedToFetch'));
      toast.error(t('drugReference.failedToFetch'));
    } finally {
      setSearching(false);
    }
  };

  // Handle quick search from recent
  const handleQuickSearch = (drugName) => {
    setSearchTerm(drugName);
    setTimeout(() => {
      handleSearch();
    }, 100);
  };

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success(t('drugReference.copied'));
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Format dosage information
  const formatDosage = (dosageArray) => {
    if (!dosageArray || !Array.isArray(dosageArray)) return [];

    return dosageArray.map(item => {
      // Extract dosage recommendations
      const sections = item.split(/(?=\d+\.\d+|\d+\s+)/g);
      return sections.filter(section => section.trim().length > 10);
    }).flat();
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Popular drug suggestions
  const popularDrugs = [
    "Atorvastatin", "Metformin", "Lisinopril", "Levothyroxine",
    "Amlodipine", "Metoprolol", "Albuterol", "Omeprazole",
    "Losartan", "Simvastatin", "Gabapentin", "Hydrochlorothiazide",
    "Sertraline", "Montelukast", "Escitalopram", "Fluoxetine"
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 8 }}>
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          borderRadius: 4,
          py: { xs: 3, md: 5 },
          px: 3,
          color: 'white',
          boxShadow: '0 20px 60px rgba(63, 81, 181, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 70%)',
          }
        }}
      >
        <Science sx={{
          fontSize: { xs: 40, md: 60 },
          mb: 2,
          opacity: 0.9,
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
        }} />
        <Typography variant="h2" fontWeight="800" gutterBottom sx={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
          {t('drugReference.title')}
        </Typography>
        <Typography variant="h6" sx={{
          opacity: 0.95,
          maxWidth: '600px',
          margin: '0 auto',
          fontWeight: 400
        }}>
          {t('drugReference.subtitle')}
        </Typography>
      </Box>
      {/* Search Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.2),
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSearch}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('drugReference.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={searching}
                  InputProps={{
                    startAdornment: (
                      <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                    sx: {
                      borderRadius: 2,
                      background: theme.palette.background.paper,
                      fontSize: '1.1rem'
                    }
                  }}
                  helperText={t('drugReference.searchHelper')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSearch}
                  disabled={searching || !searchTerm.trim()}
                  startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <Search />}
                  sx={{
                    height: 56,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    }
                  }}
                >
                  {searching ? t('drugReference.searchingButton') : t('drugReference.searchButton')}
                </Button>
              </Grid>
            </Grid>
          </form>

          {/* Popular Searches */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              {t('drugReference.popularDrugs')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {popularDrugs.map((drug) => (
                <Chip
                  key={drug}
                  label={drug}
                  onClick={() => handleQuickSearch(drug)}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Main Content Area */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 4,
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 24 }
          }}
          action={
            <Button color="inherit" size="small" onClick={() => setError("")}>
              {t('drugReference.dismiss')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {searching ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
          flexDirection: 'column',
          gap: 3
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            {t('drugReference.searchingTitle', { term: searchTerm })}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('drugReference.searchingSubtitle')}
          </Typography>
        </Box>
      ) : drugData ? (
        <Box>
          {/* Drug Header Info */}
          <Card sx={{
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}>
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      width: 60,
                      height: 60
                    }}>
                      <MedicalInformation sx={{ fontSize: 30 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h3" fontWeight="800" color="primary.main">
                        {drugData.name}
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        {t('drugReference.officialInfo')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    {drugData.rxNormId && drugData.rxNormId[0] && (
                      <Chip
                        icon={<Description />}
                        label={`${t('drugReference.rxNormId')}: ${drugData.rxNormId[0]}`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {drugData.openfdaId && (
                      <Chip
                        icon={<OpenInNew />}
                        label="OpenFDA Data Available"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      icon={<SafetyDivider />}
                      label={t('common.verified')}
                      color="info"
                      variant="outlined"
                    />
                  </Box>

                  {drugData.url && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <LinkIcon sx={{ color: 'text.secondary' }} />
                      <Link
                        href={drugData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {t('drugReference.viewFdaLabel')}
                        <OpenInNew sx={{ fontSize: 16 }} />
                      </Link>
                    </Box>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Card sx={{
                    borderRadius: 2,
                    background: alpha(theme.palette.background.paper, 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        {t('drugReference.quickActions')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={() => window.open(drugData.url, '_blank')}
                          size="small"
                          fullWidth
                        >
                          {t('drugReference.downloadLabel')}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<ContentCopy />}
                          onClick={() => copyToClipboard(JSON.stringify(drugData, null, 2))}
                          size="small"
                          fullWidth
                          color={copied ? "success" : "primary"}
                        >
                          {copied ? t('drugReference.copied') : t('drugReference.copyAllData')}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<NoteAdd />}
                          onClick={() => {
                            // Could be extended to save to local notes
                            toast.info(t('receipt.emailComingSoon'));
                          }}
                          size="small"
                          fullWidth
                        >
                          {t('drugReference.saveToNotes')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs for Detailed Information */}
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.2),
            overflow: 'hidden'
          }}>
            <CardContent sx={{ p: 0 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  background: alpha(theme.palette.background.default, 0.5),
                  px: 2,
                  '& .MuiTab-root': {
                    fontWeight: '600',
                    minHeight: 64,
                    fontSize: '0.9375rem',
                    textTransform: 'none',
                    padding: '12px 24px',
                    minWidth: 'auto',
                    '&.Mui-selected': {
                      color: theme.palette.primary.main,
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  }
                }}
              >
                <Tab
                  icon={<MedicalInformation />}
                  iconPosition="start"
                  label={t('drugReference.usesTab')}
                />
                <Tab
                  icon={<Timeline />}
                  iconPosition="start"
                  label={t('drugReference.dosageTab')}
                />
                <Tab
                  icon={<Warning />}
                  iconPosition="start"
                  label={t('drugReference.warningsTab')}
                />
                <Tab
                  icon={<Info />}
                  iconPosition="start"
                  label={t('drugReference.additionalInfoTab')}
                />
              </Tabs>

              {/* Uses & Indications Tab */}
              {activeTab === 0 && drugData.uses && (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
                    {t('drugReference.indicationsAndUses')}
                  </Typography>
                  <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    background: alpha(theme.palette.primary.main, 0.03),
                    borderLeft: `4px solid ${theme.palette.primary.main}`
                  }}>
                    {drugData.uses.map((use, index) => (
                      <Box key={index} sx={{ mb: 3 }}>
                        <Typography variant="body1" paragraph>
                          {use}
                        </Typography>
                        <Divider sx={{ my: 2, opacity: 0.3 }} />
                      </Box>
                    ))}
                  </Box>

                  {/* Quick Summary */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {t('drugReference.quickSummary')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocalHospital sx={{ color: 'primary.main' }} />
                              <Typography variant="subtitle2" fontWeight="600">
                                {t('drugReference.primaryUses')}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {drugData.name} is primarily used for cholesterol management and cardiovascular risk reduction.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Psychology sx={{ color: 'info.main' }} />
                              <Typography variant="subtitle2" fontWeight="600">
                                {t('drugReference.mechanism')}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              HMG-CoA reductase inhibitor (statin) that lowers LDL cholesterol production.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}

              {/* Dosage Information Tab */}
              {activeTab === 1 && drugData.dosage && (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
                    {t('drugReference.dosageAndAdministration')}
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('drugReference.dosageImportant')}
                    </Typography>
                  </Alert>

                  <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    background: alpha(theme.palette.info.main, 0.05),
                    borderLeft: `4px solid ${theme.palette.info.main}`
                  }}>
                    {formatDosage(drugData.dosage).map((dosageSection, index) => (
                      <Box key={index} sx={{ mb: 3 }}>
                        <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                          {dosageSection}
                        </Typography>
                        <Divider sx={{ my: 2, opacity: 0.3 }} />
                      </Box>
                    ))}
                  </Box>

                  {/* Dosage Table */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {t('drugReference.standardDosageGuidelines')}
                    </Typography>
                    <TableContainer sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      mb: 3
                    }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.info.main, 0.04) }}>
                            <TableCell><strong>{t('drugReference.patientGroup')}</strong></TableCell>
                            <TableCell><strong>{t('drugReference.startingDose')}</strong></TableCell>
                            <TableCell><strong>{t('drugReference.maintenanceRange')}</strong></TableCell>
                            <TableCell><strong>{t('drugReference.maximumDose')}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow hover>
                            <TableCell>Adults (General)</TableCell>
                            <TableCell>10-20 mg once daily</TableCell>
                            <TableCell>10-40 mg once daily</TableCell>
                            <TableCell>80 mg once daily</TableCell>
                          </TableRow>
                          <TableRow hover>
                            <TableCell>Pediatric (HeFH)</TableCell>
                            <TableCell>10 mg once daily</TableCell>
                            <TableCell>10-20 mg once daily</TableCell>
                            <TableCell>20 mg once daily</TableCell>
                          </TableRow>
                          <TableRow hover>
                            <TableCell>High Risk Patients</TableCell>
                            <TableCell>40 mg once daily</TableCell>
                            <TableCell>40-80 mg once daily</TableCell>
                            <TableCell>80 mg once daily</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}

              {/* Warnings & Precautions Tab */}
              {activeTab === 2 && drugData.warnings && (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom color="error">
                    {t('drugReference.warningsAndPrecautions')}
                  </Typography>

                  <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500">
                      {t('drugReference.criticalWarnings')}
                    </Typography>
                  </Alert>

                  <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    background: alpha(theme.palette.error.main, 0.05),
                    borderLeft: `4px solid ${theme.palette.error.main}`
                  }}>
                    {drugData.warnings.map((warning, index) => (
                      <Box key={index} sx={{ mb: 3 }}>
                        <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                          {warning}
                        </Typography>
                        <Divider sx={{ my: 2, opacity: 0.3 }} />
                      </Box>
                    ))}
                  </Box>

                  {/* Key Risk Factors */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {t('drugReference.keyRiskFactors')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{
                          borderRadius: 2,
                          border: `2px solid ${theme.palette.error.main}`,
                          background: alpha(theme.palette.error.main, 0.03)
                        }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Warning sx={{ color: 'error.main' }} />
                              <Typography variant="subtitle2" fontWeight="600" color="error.main">
                                {t('drugReference.highRiskConditions')}
                              </Typography>
                            </Box>
                            <List dense>
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Error sx={{ color: 'error.main', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Myopathy & Rhabdomyolysis"
                                  secondary="Muscle pain, weakness, dark urine"
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Error sx={{ color: 'error.main', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Hepatic Dysfunction"
                                  secondary="Liver enzyme elevations, jaundice"
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Error sx={{ color: 'error.main', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Renal Impairment"
                                  secondary="Increased risk of complications"
                                />
                              </ListItem>
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{
                          borderRadius: 2,
                          border: `2px solid ${theme.palette.warning.main}`,
                          background: alpha(theme.palette.warning.main, 0.03)
                        }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Warning sx={{ color: 'warning.main' }} />
                              <Typography variant="subtitle2" fontWeight="600" color="warning.main">
                                {t('drugReference.drugInteractions')}
                              </Typography>
                            </Box>
                            <List dense>
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Inventory sx={{ color: 'warning.main', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Other Statins"
                                  secondary="Increased risk of side effects"
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Inventory sx={{ color: 'warning.main', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Certain Antibiotics"
                                  secondary="Clarithromycin, erythromycin"
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Inventory sx={{ color: 'warning.main', fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Antifungal Agents"
                                  secondary="Itraconazole, ketoconazole"
                                />
                              </ListItem>
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}

              {/* Additional Information Tab */}
              {activeTab === 3 && (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom color="primary">
                    {t('drugReference.additionalInfoTab')}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="600" gutterBottom>
                            {t('drugReference.dataSources')}
                          </Typography>
                          <List>
                            <ListItem>
                              <ListItemIcon>
                                <OpenInNew />
                              </ListItemIcon>
                              <ListItemText
                                primary={t('drugReference.openFdaDatabase')}
                                secondary={t('drugReference.officialFdaLabeling')}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <School />
                              </ListItemIcon>
                              <ListItemText
                                primary={t('drugReference.rxNorm')}
                                secondary={t('drugReference.standardizedNomenclature')}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <Description />
                              </ListItemIcon>
                              <ListItemText
                                primary={t('drugReference.fdaLabels')}
                                secondary={t('drugReference.fullPrescribingInfo')}
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="600" gutterBottom>
                            {t('drugReference.technicalDetails')}
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell><strong>{t('drugReference.drugName')}</strong></TableCell>
                                <TableCell>{drugData.name}</TableCell>
                              </TableRow>
                              {drugData.rxNormId?.[0] && (
                                <TableRow>
                                  <TableCell><strong>{t('drugReference.rxNormId')}</strong></TableCell>
                                  <TableCell>{drugData.rxNormId[0]}</TableCell>
                                </TableRow>
                              )}
                              {drugData.openfdaId && (
                                <TableRow>
                                  <TableCell><strong>{t('drugReference.openFdaId')}</strong></TableCell>
                                  <TableCell>{drugData.openfdaId}</TableCell>
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell><strong>{t('drugReference.source')}</strong></TableCell>
                                <TableCell>{drugData.source || t('drugReference.fdaDatabase')}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>{t('drugReference.lastUpdated')}</strong></TableCell>
                                <TableCell>{new Date().toLocaleDateString()}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Disclaimer */}
                  <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>{t('drugReference.disclaimerTitle')}</strong> {t('drugReference.disclaimerText')}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      ) : (
        /* Welcome/Empty State */
        (<Card sx={{
          textAlign: 'center',
          py: 8,
          background: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          mb: 4
        }}>
          <CardContent>
            <Science sx={{
              fontSize: 80,
              color: 'primary.main',
              mb: 2,
              opacity: 0.8
            }} />
            <Typography variant="h5" color="primary.main" fontWeight="600" gutterBottom>
              {t('drugReference.searchDrugInfo')}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 3 }}>
              {t('drugReference.welcomeText')}
            </Typography>

            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  {t('drugReference.recentSearches')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {recentSearches.map((search, index) => (
                    <Chip
                      key={index}
                      label={search.name}
                      onClick={() => handleQuickSearch(search.name)}
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>)
      )}
    </Container>
  );
}

// Add missing imports
